import React, { useState, useEffect } from "react";
import { FaSearch, FaTimes, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useSearch } from "../context/SearchContext";

export default function SearchBar({ alwaysVisible = false }) {
  const { searchTerm, setSearchTerm } = useSearch();
  const [isSearchVisible, setIsSearchVisible] = useState(alwaysVisible);
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);

  // Clear search when search term is empty
  useEffect(() => {
    if (searchTerm === "") {
      clearSearch();
    } else {
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const performSearch = () => {
    // Clear previous highlights
    clearSearch();

    if (searchTerm.length < 2) return;

    // Get all text nodes in the page
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          // Skip script, style, and other non-visible elements
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_SKIP;
          
          const tagName = parent.tagName.toLowerCase();
          if (['script', 'style', 'noscript', 'iframe', 'object'].includes(tagName)) {
            return NodeFilter.FILTER_SKIP;
          }
          
          // Skip if the text doesn't contain our search term
          if (!node.textContent.toLowerCase().includes(searchTerm.toLowerCase())) {
            return NodeFilter.FILTER_SKIP;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      },
      false
    );

    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    const results = [];
    textNodes.forEach((textNode, nodeIndex) => {
      const text = textNode.textContent;
      const searchRegex = new RegExp(`(${searchTerm})`, 'gi');
      const matches = [...text.matchAll(searchRegex)];
      
      matches.forEach((match, matchIndex) => {
        results.push({
          node: textNode,
          nodeIndex,
          matchIndex,
          text: match[0],
          index: match.index
        });
      });
    });

    // Highlight all results
    results.forEach((result, resultIndex) => {
      highlightText(result, resultIndex);
    });

    setSearchResults(results);
    if (results.length > 0) {
      setCurrentResultIndex(0);
      scrollToResult(0);
    }
  };

  const highlightText = (result, resultIndex) => {
    const { node, text, index } = result;
    const beforeText = node.textContent.substring(0, index);
    const afterText = node.textContent.substring(index + text.length);
    
    // Create highlight span
    const highlightSpan = document.createElement('span');
    highlightSpan.className = `search-highlight ${resultIndex === currentResultIndex ? 'search-highlight-current' : ''}`;
    highlightSpan.setAttribute('data-search-result', resultIndex);
    highlightSpan.textContent = text;
    
    // Create text nodes for before and after
    const beforeNode = document.createTextNode(beforeText);
    const afterNode = document.createTextNode(afterText);
    
    // Replace the original text node
    const parent = node.parentNode;
    parent.insertBefore(beforeNode, node);
    parent.insertBefore(highlightSpan, node);
    parent.insertBefore(afterNode, node);
    parent.removeChild(node);
  };

  const clearSearch = () => {
    // Remove all highlight spans and restore original text
    const highlights = document.querySelectorAll('.search-highlight');
    highlights.forEach(highlight => {
      const parent = highlight.parentNode;
      const textNode = document.createTextNode(highlight.textContent);
      parent.replaceChild(textNode, highlight);
      parent.normalize(); // Merge adjacent text nodes
    });
    setSearchResults([]);
    setCurrentResultIndex(-1);
  };

  const scrollToResult = (index) => {
    const highlight = document.querySelector(`[data-search-result="${index}"]`);
    if (highlight) {
      // Update current highlight
      document.querySelectorAll('.search-highlight-current').forEach(el => {
        el.classList.remove('search-highlight-current');
      });
      highlight.classList.add('search-highlight-current');
      
      // Scroll to element
      highlight.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const navigateResults = (direction) => {
    if (searchResults.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = currentResultIndex >= searchResults.length - 1 ? 0 : currentResultIndex + 1;
    } else {
      newIndex = currentResultIndex <= 0 ? searchResults.length - 1 : currentResultIndex - 1;
    }
    
    setCurrentResultIndex(newIndex);
    scrollToResult(newIndex);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        navigateResults('prev');
      } else {
        navigateResults('next');
      }
    } else if (e.key === 'Escape') {
      setSearchTerm("");
      if (!alwaysVisible) {
        setIsSearchVisible(false);
      }
    }
  };

  if (alwaysVisible) {
    // Always visible inline search bar for article filtering
    return (
      <div className="relative w-full">
        <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
          className="w-full h-9 bg-white/10 border border-white/10 rounded-full pl-9 pr-9 text-sm text-white placeholder-gray-500 transition-colors focus:outline-none focus:bg-white/15 focus:border-white/25"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
          >
            <FaTimes className="text-xs" />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Search Toggle Button */}
      <div className="relative">
        <button
          onClick={() => setIsSearchVisible(!isSearchVisible)}
          className="p-2 text-white/70 hover:text-white transition-colors"
          title="Search page"
        >
          <FaSearch className="text-lg" />
        </button>
      </div>

      {/* Search Bar */}
      {isSearchVisible && (
        <div className="absolute top-full left-0 right-0 bg-black/40 backdrop-blur-xl border-b border-white/10 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center space-x-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search on page..."
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40"
                  autoFocus
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <FaTimes className="text-sm" />
                  </button>
                )}
              </div>

              {/* Search Results Counter */}
              {searchResults.length > 0 && (
                <div className="text-sm text-white/70 whitespace-nowrap">
                  {currentResultIndex + 1} of {searchResults.length}
                </div>
              )}

              {/* Navigation Buttons */}
              {searchResults.length > 0 && (
                <div className="flex space-x-1">
                  <button
                    onClick={() => navigateResults('prev')}
                    className="p-2 text-white/70 hover:text-white transition-colors"
                    title="Previous result (Shift+Enter)"
                  >
                    <FaChevronUp className="text-sm" />
                  </button>
                  <button
                    onClick={() => navigateResults('next')}
                    className="p-2 text-white/70 hover:text-white transition-colors"
                    title="Next result (Enter)"
                  >
                    <FaChevronDown className="text-sm" />
                  </button>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => {
                  setSearchTerm("");
                  setIsSearchVisible(false);
                }}
                className="p-2 text-white/70 hover:text-white transition-colors"
                title="Close search (Esc)"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Search Tips */}
            <div className="mt-2 text-xs text-white/50">
              Press Enter to go to next result, Shift+Enter for previous, Esc to close
            </div>
          </div>
        </div>
      )}

      {/* Add custom styles for highlighting */}
      <style jsx global>{`
        .search-highlight {
          background-color: rgba(255, 255, 0, 0.3);
          padding: 1px 2px;
          border-radius: 2px;
        }
        .search-highlight-current {
          background-color: rgba(255, 165, 0, 0.6);
          font-weight: bold;
        }
      `}</style>
    </>
  );
}
